import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import GroupIcon from '@mui/icons-material/Group';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonIcon from '@mui/icons-material/Person';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GavelIcon from '@mui/icons-material/Gavel';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { useAuth } from '../../contexts/AuthContext';
import { useRealEstate } from '../../contexts/RealEstateContext';
import { StatCardSkeleton, ContentCardSkeleton } from '../../components/common';
import { C21_COLORS } from '../../constants/realEstate';
import PropertyStatusReport from './PropertyStatusReport';
import BuyerActivityReport from './BuyerActivityReport';

const RealEstateDashboard = () => {
  const { user, getActiveCompany } = useAuth();
  const { getStatistics } = useRealEstate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Loading state - hooks must be called before any early return
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);

  // Simulate loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  // Check if Century 21 is selected - redirect to regular Dashboard if not
  const activeCompany = getActiveCompany();
  if (activeCompany?.id !== 'century21') {
    return <Navigate to="/" replace />;
  }

  const stats = getStatistics();

  const summaryData = [
    {
      id: 'properties',
      title: 'Total Listings',
      value: stats.totalProperties,
      icon: HomeIcon,
      color: C21_COLORS.gold,
    },
    {
      id: 'active',
      title: 'Active Listings',
      value: stats.activeListings,
      icon: TrendingUpIcon,
      color: '#2e7d32',
    },
    {
      id: 'contract',
      title: 'On Contract',
      value: stats.onContractProperties,
      icon: AssignmentIcon,
      color: C21_COLORS.darkGold,
    },
    {
      id: 'closing',
      title: 'Closing',
      value: stats.closingProperties,
      icon: GavelIcon,
      color: C21_COLORS.warmBrown,
    },
    {
      id: 'buyers',
      title: 'Total Buyers',
      value: stats.totalBuyers,
      icon: GroupIcon,
      color: C21_COLORS.black,
    },
    {
      id: 'preapproved',
      title: 'Pre-Approved',
      value: stats.preApprovedBuyers,
      icon: PersonIcon,
      color: C21_COLORS.darkGray,
    },
    {
      id: 'viewings',
      title: 'Property Viewings',
      value: stats.totalViewings,
      icon: VisibilityIcon,
      color: C21_COLORS.gold,
    },
    {
      id: 'avgPrice',
      title: 'Avg. Price',
      value: `$${Math.round(stats.avgPrice / 1000)}K`,
      icon: AttachMoneyIcon,
      color: C21_COLORS.darkGold,
    },
  ];

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: { xs: 2, sm: 4 } }}>
        <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={700} sx={{ mb: 0.5 }}>
          Real Estate Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
          Welcome back, {user?.name || 'User'}. Here's your property portfolio overview.
        </Typography>
      </Box>

      {/* Statistics Cards */}
      {isLoading ? (
        <StatCardSkeleton count={8} />
      ) : (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)', lg: 'repeat(8, 1fr)' },
          gap: { xs: 1.5, sm: 2 },
          mb: { xs: 3, sm: 4 }
        }}>
          {summaryData.map((item) => {
            const IconComponent = item.icon;
            return (
              <Card
                key={item.id}
                elevation={0}
                sx={{
                  borderRadius: 2,
                  transition: 'all 0.2s ease-in-out',
                  cursor: 'pointer',
                  backgroundColor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  '&:hover': {
                    borderColor: C21_COLORS.gold,
                    boxShadow: '0 4px 12px rgba(197,163,109,0.15)',
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box
                      sx={{
                        p: 0.75,
                        borderRadius: 1.5,
                        backgroundColor: `${item.color}12`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <IconComponent sx={{ color: item.color, fontSize: { xs: 18, sm: 20 } }} />
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' }, mb: 0.25 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, lineHeight: 1.2 }}>
                    {item.value}
                  </Typography>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      {/* Tabs for Reports */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'grey.200',
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              px: { xs: 2, sm: 3 },
              '& .MuiTabs-indicator': {
                backgroundColor: C21_COLORS.gold,
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: { xs: '0.85rem', sm: '0.9rem' },
                minHeight: 56,
                '&.Mui-selected': {
                  color: C21_COLORS.black,
                },
              },
            }}
          >
            <Tab
              icon={<HomeIcon sx={{ fontSize: 20 }} />}
              iconPosition="start"
              label="Property Status"
            />
            <Tab
              icon={<GroupIcon sx={{ fontSize: 20 }} />}
              iconPosition="start"
              label="Buyer Activity"
            />
          </Tabs>
        </Box>

        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {isLoading ? (
            <ContentCardSkeleton lines={10} />
          ) : (
            <>
              {activeTab === 0 && <PropertyStatusReport />}
              {activeTab === 1 && <BuyerActivityReport />}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default RealEstateDashboard;
